/**
 * <CodexPanel /> — Session 8.
 *
 * Progressive discovery Codex:
 *   - Enemies tab: 6 pillars per enemy (name/element/resistances/weaknesses/
 *     drops/abilities). Unlocked pillars show; unknown ones read "???".
 *   - Elements tab: 10-element interaction matrix, dimmed for elements the
 *     player has never seen.
 *   - Progress bars + milestone hint per entry.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Skull, Sparkles, Sword, HelpCircle, Award, Target, ArrowUpDown, X, RefreshCw, Flame, Image as ImageIcon } from "lucide-react";
import { ElementBadge } from "./ElementBadge";
import { ELEMENT_META, normalizeElement } from "../constants/elements";
import { TRAVEL_VISIONS, loadUnlockedVisions, loadVisionFirstSeen } from "@/lib/travelVisions";
import "@/gm/gm-animations.css";

const TABS = [
  { id: "enemies",   label: "Enemies",   Icon: Skull },
  { id: "hunts",     label: "Hunts",     Icon: Target },
  { id: "visions",   label: "Visioni",   Icon: ImageIcon },
  { id: "elements",  label: "Elements",  Icon: Sparkles },
  { id: "abilities", label: "Abilities", Icon: Sword },
];

const RARITY = "#B89C3A";

// Rank ordering — lowest → highest for sorting the bestiary. Anything
// not listed here (custom GM ranks) falls to the bottom.
const RANK_ORDER = { "Trash": 0, "Common": 1, "Elite": 2, "Boss": 3, "Leggenda": 4 };

const SORT_OPTIONS = [
  { id: "progress", label: "Progresso" },
  { id: "level",    label: "Livello" },
  { id: "rank",     label: "Rank" },
  { id: "name",     label: "Nome" },
];


function ProgressBar({ pct, height = 4 }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="w-full bg-[#161616] border border-[#262626]" style={{ height }}>
      <div style={{ width: `${clamped}%`, height: "100%", backgroundColor: RARITY }} />
    </div>
  );
}

function OverviewCard({ overall, totals }) {
  return (
    <div className="panel p-3 space-y-2" data-testid="codex-overview">
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">Chronicler's Ledger</div>
      {[
        { label: "Beasts Catalogued", pct: overall?.enemies_pct   || 0, denom: totals?.enemies },
        { label: "Elements Witnessed", pct: overall?.elements_pct || 0, denom: totals?.elements },
        { label: "Techniques Observed", pct: overall?.abilities_pct || 0, denom: totals?.abilities },
      ].map(row => (
        <div key={row.label}>
          <div className="flex items-center justify-between font-mono text-[10px] text-[#B0ADA5] mb-0.5">
            <span>{row.label}</span>
            <span className="text-[#E5C34A]">{row.pct}%</span>
          </div>
          <ProgressBar pct={row.pct} />
        </div>
      ))}
    </div>
  );
}


function EnemyRow({ e }) {
  const pct = e.progress?.pct || 0;
  const discovered = e.progress?.discovered || [];
  const has = (p) => discovered.includes(p);
  const animClasses = (e.animations || []).map(a => `gm-anim-${a}`).join(" ") || "gm-anim-idle-float";
  return (
    <div className="panel-inner p-2 space-y-1.5" data-testid={`codex-enemy-${e.id}`}>
      <div className="flex items-start justify-between gap-2">
        {/* §5 image + animation shown even without full name discovery — the
            player has already seen the creature at least once. */}
        <div className="shrink-0 w-14 h-14 border border-[#333] bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
          {e.image ? (
            <img src={e.image} alt="" className={`w-full h-full object-cover ${animClasses}`} />
          ) : (
            <Skull className={`w-8 h-8 text-[#666] ${animClasses}`} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`font-heading text-sm ${has("name") ? "text-[#E5C34A]" : "text-[#666]"}`}>
            {has("name") ? e.name : "??? ???"}
          </div>
          <div className="font-mono text-[10px] text-[#8c8c8c] mt-0.5">
            {has("name") ? `Lv ${e.level ?? "?"}` : "Prima osservazione — poche informazioni."}
          </div>
          {has("name") && (
            <div className="grid grid-cols-3 gap-x-2 gap-y-0.5 mt-1 font-mono text-[9px] text-[#B0ADA5]" data-testid={`codex-enemy-stats-${e.id}`}>
              <span title="Max HP" className="text-[#6E8B3D]">HP <span className="text-[#E2DFD8]">{e.max_hp ?? e.hp ?? "?"}</span></span>
              <span title="Damage" className="text-[#E33333]">DMG <span className="text-[#E2DFD8]">{e.attack ?? "?"}</span></span>
              <span title="Armor" className="text-[#6FA8DC]">ARM <span className="text-[#E2DFD8]">{e.defense ?? "?"}</span></span>
              {e.speed != null && <span title="Speed" className="text-[#B89C3A]">SPD <span className="text-[#E2DFD8]">{e.speed}</span></span>}
              {e.rank && <span title="Rank" className="text-[#A97AB0]">{e.rank}</span>}
              {e.type && <span title="Type" className="text-[#8c8c8c]">{e.type}</span>}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-xs text-[#E5C34A]">{pct}%</div>
          <ProgressBar pct={pct} height={3} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1 border-t border-[#161616]">
        <PillarRow label="Element" ok={has("element")}>
          {has("element") && e.element && <ElementBadge element={e.element} size="sm" showLabel={false} />}
        </PillarRow>
        <PillarRow label="Resistances" ok={has("resistances")}>
          {has("resistances") && (e.resistances || []).map(el => (
            <ElementBadge key={"r" + el} element={el} size="sm" showLabel={false} />
          ))}
        </PillarRow>
        <PillarRow label="Weaknesses" ok={has("weaknesses")}>
          {has("weaknesses") && (e.weaknesses || []).map(el => (
            <ElementBadge key={"w" + el} element={el} size="sm" showLabel={false} />
          ))}
        </PillarRow>
        <PillarRow label="Drops" ok={has("drops")}>
          {has("drops") && (e.drops || []).slice(0, 3).map((d, i) => (
            <span key={i} className="font-mono text-[9px] text-[#B0ADA5]">
              {typeof d === "string" ? d : (d.name || "loot")}
            </span>
          ))}
        </PillarRow>
        <PillarRow label="Abilities" ok={has("abilities")}>
          {has("abilities") && (e.abilities || []).slice(0, 3).map((a, i) => (
            <span key={i} className="font-mono text-[9px] text-[#B0ADA5]">
              {typeof a === "string" ? a : (a.name || "skill")}
            </span>
          ))}
        </PillarRow>
        <PillarRow label="Lore" ok={has("name") && !!e.lore}>
          {has("name") && e.lore && (
            <span className="font-mono text-[9px] italic text-[#8c8c8c] line-clamp-2">{e.lore}</span>
          )}
        </PillarRow>
      </div>
    </div>
  );
}

function PillarRow({ label, ok, children }) {
  return (
    <div className="flex items-start gap-1.5 text-[10px]">
      {ok ? (
        <Award size={10} strokeWidth={1.8} className="text-[#6E8B3D] mt-0.5" />
      ) : (
        <HelpCircle size={10} strokeWidth={1.8} className="text-[#333] mt-0.5" />
      )}
      <div className="font-mono text-[9px] uppercase tracking-wider text-[#8c8c8c] w-20 shrink-0">{label}</div>
      <div className="flex flex-wrap gap-1 flex-1">
        {ok ? children : <span className="italic text-[#666]">Unknown</span>}
      </div>
    </div>
  );
}


// ── Vision Codex ──────────────────────────────────────────────────
// Collects every Ravencross panorama the player has ever glimpsed on
// the road. Unlocks live in localStorage so nothing needs a backend
// migration; a click on a card re-shows the full-size artwork.

function VisionsView() {
  const [unlocked, setUnlocked] = useState(() => loadUnlockedVisions());
  const [firstSeen, setFirstSeen] = useState(() => loadVisionFirstSeen());
  const [zoom, setZoom] = useState(null);
  const [sortMode, setSortMode] = useState("default"); // "default" | "chrono"

  useEffect(() => {
    setUnlocked(loadUnlockedVisions());
    setFirstSeen(loadVisionFirstSeen());
  }, []);

  const total = TRAVEL_VISIONS.length;
  const found = unlocked.size;

  const fmtDate = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    } catch { return null; }
  };

  // §CHRONO-SORT: reorder unlocked cards by first-seen timestamp when
  // Cronologia is active. Locked cards stay pinned to the bottom so
  // players see their journey history first.
  const orderedVisions = useMemo(() => {
    if (sortMode !== "chrono") return TRAVEL_VISIONS;
    const withMeta = TRAVEL_VISIONS.map(v => ({
      ...v,
      _seenAt: firstSeen[v.id] ? new Date(firstSeen[v.id]).getTime() : null,
    }));
    return withMeta.sort((a, b) => {
      if (a._seenAt == null && b._seenAt == null) return 0;
      if (a._seenAt == null) return 1;
      if (b._seenAt == null) return -1;
      return a._seenAt - b._seenAt;
    });
  }, [sortMode, firstSeen]);

  return (
    <div className="space-y-2" data-testid="codex-visions">
      <div className="panel p-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#B89C3A]">
              Visioni dal Cammino
            </div>
            <div className="font-mono text-[10px] text-[#8c8c8c] mt-1">
              Panorami di Ravencross incontrati durante il viaggio.
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-lg text-[#E5C34A]" data-testid="visions-count">
              {found}<span className="text-[#666] text-sm"> / {total}</span>
            </div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
              Scoperte
            </div>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-[#161616] flex items-center gap-1"
             data-testid="visions-sort-bar">
          <span className="font-mono text-[9px] tracking-widest uppercase text-[#666] mr-1">
            Ordina
          </span>
          <button
            onClick={() => setSortMode("default")}
            data-testid="visions-sort-default"
            className={`px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase border transition-colors
              ${sortMode === "default" ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-[#262626] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}
          >
            Predefinito
          </button>
          <button
            onClick={() => setSortMode("chrono")}
            data-testid="visions-sort-chrono"
            className={`px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase border transition-colors
              ${sortMode === "chrono" ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-[#262626] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}
          >
            Cronologia
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="visions-grid">
        {orderedVisions.map(v => {
          const ok = unlocked.has(v.id);
          return (
            <button
              key={v.id}
              type="button"
              disabled={!ok}
              onClick={() => ok && setZoom(v)}
              data-testid={`vision-card-${v.id}`}
              className={`panel-inner block text-left overflow-hidden border transition-transform
                ${ok ? "border-[#B89C3A] hover:scale-[1.01] cursor-pointer"
                     : "border-[#262626] opacity-50 cursor-not-allowed"}`}
            >
              <div className="relative aspect-[3/2] bg-black">
                {ok ? (
                  <img
                    src={v.url}
                    alt={v.caption}
                    className="w-full h-full object-cover"
                    style={{ imageRendering: "pixelated" }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <HelpCircle className="w-8 h-8 text-[#333]" strokeWidth={1.2} />
                  </div>
                )}
              </div>
              <div className="px-2 py-1.5 border-t border-[#161616]">
                <div className="flex items-center justify-between gap-2">
                  <div className={`font-heading text-[11px] tracking-widest uppercase truncate ${ok ? "text-[#E5C34A]" : "text-[#555]"}`}>
                    {ok ? v.caption.replace(/\.$/, "") : "??? ???"}
                  </div>
                  {ok && firstSeen[v.id] && (
                    <span
                      data-testid={`vision-date-${v.id}`}
                      className="shrink-0 font-mono text-[8px] tracking-widest uppercase text-[#B89C3A] border border-[#B89C3A]/40 px-1.5 py-0.5"
                      title={`Scoperta il ${new Date(firstSeen[v.id]).toLocaleString()}`}
                    >
                      {fmtDate(firstSeen[v.id])}
                    </span>
                  )}
                </div>
                {!ok && (
                  <div className="font-mono text-[9px] text-[#666] mt-0.5">
                    Continua a camminare per rivelarla.
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {zoom && (
        <div
          data-testid="vision-zoom-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
          onClick={() => setZoom(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setZoom(null)}
              data-testid="vision-zoom-close"
              className="absolute -top-3 -right-3 z-10 p-1.5 bg-[#0a0a0a] border border-[#B89C3A] text-[#E5C34A] hover:bg-[#B89C3A]/20"
              aria-label="Chiudi"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <div className="border-2 border-[#B89C3A] bg-black shadow-[0_0_60px_rgba(184,156,58,0.35)]">
              <img
                src={zoom.url}
                alt={zoom.caption}
                className="w-full h-auto block"
                style={{ imageRendering: "pixelated" }}
              />
              <div className="px-4 py-2 border-t border-[#B89C3A]/40">
                <span className="font-body italic text-sm text-[#E2DFD8]">{zoom.caption}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ElementMatrix({ data }) {
  if (!data) return null;
  const els = data.elements || [];
  const matrix = data.matrix || {};
  const seenMap = Object.fromEntries(els.map(e => [e.name, e.seen]));
  return (
    <div className="panel p-3 overflow-auto" data-testid="codex-element-matrix">
      <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] mb-2">
        Elemental Interactions · attacker → defender
      </div>
      <table className="text-[9px] font-mono border-collapse min-w-max">
        <thead>
          <tr>
            <th className="p-1 border border-[#262626]"></th>
            {els.map(e => (
              <th key={e.name} className="p-1 border border-[#262626] text-center"
                  style={{ color: seenMap[e.name] ? ELEMENT_META[e.name]?.color : "#333" }}>
                {e.name.slice(0, 3).toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {els.map(attRow => {
            const attSeen = seenMap[attRow.name];
            return (
              <tr key={attRow.name}>
                <th className="p-1 border border-[#262626] text-right"
                    style={{ color: attSeen ? ELEMENT_META[attRow.name]?.color : "#333" }}>
                  {attRow.name}
                </th>
                {els.map(defCell => {
                  const defSeen = seenMap[defCell.name];
                  const cell = matrix[attRow.name]?.[defCell.name] ?? 1.0;
                  const visible = attSeen && defSeen;
                  const bg = cell > 1 ? "rgba(110,139,61,0.15)" : cell < 1 ? "rgba(227,51,51,0.15)" : "transparent";
                  return (
                    <td key={defCell.name}
                        className="p-1 border border-[#262626] text-center"
                        style={{
                          backgroundColor: visible ? bg : "transparent",
                          color: visible ? "#B0ADA5" : "#333",
                        }}>
                      {visible ? Math.round(cell * 100) + "%" : "?"}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


export default function CodexPanel({ ch }) {
  const [tab, setTab] = useState("enemies");
  const [data, setData] = useState(null);
  const [elementData, setElementData] = useState(null);
  const [hunts, setHunts] = useState(null);
  const [huntsBusy, setHuntsBusy] = useState(false);
  const [sort, setSort] = useState("progress");
  const [loading, setLoading] = useState(true);

  const reloadHunts = async () => {
    try { setHunts(await api.codexHunts()); }
    catch { /* silent — non-critical */ }
  };

  useEffect(() => {
    let dead = false;
    setLoading(true);
    Promise.all([api.codexHome(), api.codexElements(), api.codexHunts().catch(() => null)])
      .then(([home, elements, h]) => { if (!dead) {
        setData(home); setElementData(elements); if (h) setHunts(h);
      }})
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, []);

  const acceptHunt = async (key) => {
    if (huntsBusy) return;
    setHuntsBusy(true);
    try {
      const r = await api.codexHuntAccept(key);
      setHunts(prev => prev ? { ...prev, active: r.active,
        available: (prev.available || []).filter(h => h.key !== key) } : prev);
      toast.success("Caccia accettata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile accettare la caccia");
    } finally { setHuntsBusy(false); }
  };

  const abandonHunt = async () => {
    if (huntsBusy) return;
    if (!window.confirm("Abbandonare la caccia in corso?")) return;
    setHuntsBusy(true);
    try {
      await api.codexHuntAbandon();
      await reloadHunts();
      toast("Caccia abbandonata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setHuntsBusy(false); }
  };

  const rerollHunts = async () => {
    if (huntsBusy) return;
    setHuntsBusy(true);
    try {
      const r = await api.codexHuntReroll();
      setHunts(prev => prev ? { ...prev, available: r.available, active: r.active } : prev);
      toast.success(`Slate rerollata (-${r.gold_spent}g)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reroll fallita");
    } finally { setHuntsBusy(false); }
  };

  const sortedEnemies = useMemo(() => {
    const list = [...(data?.enemies || [])];
    switch (sort) {
      case "level":
        return list.sort((a, b) => (a.level || 0) - (b.level || 0));
      case "rank":
        return list.sort((a, b) =>
          (RANK_ORDER[a.rank] ?? 99) - (RANK_ORDER[b.rank] ?? 99)
          || (a.level || 0) - (b.level || 0));
      case "name":
        return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      case "progress":
      default:
        return list.sort((a, b) => (b.progress?.pct || 0) - (a.progress?.pct || 0));
    }
  }, [data, sort]);

  if (loading) return <div className="italic text-xs text-[#666] p-4">Opening the Codex...</div>;
  if (!data)  return <div className="italic text-xs text-[#E33333] p-4">Could not open the Codex.</div>;

  return (
    <div className="space-y-2" data-testid="codex-panel">
      <OverviewCard overall={data.overall} totals={data.totals} />

      <div className="panel p-2 flex flex-wrap gap-1" data-testid="codex-tabs">
        {TABS.map(t => {
          const Icon = t.Icon;
          const active = tab === t.id;
          const badge = t.id === "hunts" && hunts?.active ? "●" : null;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`codex-tab-${t.id}`}
              className={`px-2 py-1 flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase border transition-colors
                ${active ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-[#262626] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}>
              <Icon size={11} strokeWidth={1.6} /> {t.label}
              {badge && <span className="text-[#E33333] ml-0.5" data-testid="codex-hunt-active-dot">{badge}</span>}
            </button>
          );
        })}
      </div>

      {tab === "enemies" && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 panel p-2" data-testid="codex-sort-bar">
            <ArrowUpDown size={10} strokeWidth={1.6} className="text-[#666]" />
            <span className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mr-1">Ordina</span>
            {SORT_OPTIONS.map(opt => (
              <button key={opt.id} onClick={() => setSort(opt.id)}
                data-testid={`codex-sort-${opt.id}`}
                className={`px-2 py-0.5 font-mono text-[9px] tracking-widest uppercase border transition-colors
                  ${sort === opt.id ? "border-[#E5C34A] text-[#E5C34A] bg-[#B89C3A]/10" : "border-[#262626] text-[#8c8c8c] hover:text-[#B0ADA5]"}`}>
                {opt.label}
              </button>
            ))}
          </div>
          {sortedEnemies.length === 0 ? (
            <div className="panel p-4 text-center italic text-[#666] text-sm">
              You have not chronicled any beasts yet. Battle brings knowledge.
            </div>
          ) : (
            sortedEnemies.map(e => <EnemyRow key={e.id} e={e} />)
          )}
        </div>
      )}

      {tab === "hunts" && (
        <HuntsView hunts={hunts} onAccept={acceptHunt} onAbandon={abandonHunt}
                   onReroll={rerollHunts} busy={huntsBusy} playerGold={ch?.gold ?? 0} />
      )}

      {tab === "visions" && <VisionsView />}

      {tab === "elements" && <ElementMatrix data={elementData} />}

      {tab === "abilities" && (
        <div className="panel p-3" data-testid="codex-abilities">
          <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c] mb-2">Techniques Observed</div>
          {(data.abilities_seen || []).length === 0 ? (
            <div className="italic text-xs text-[#666]">
              No techniques recorded. Witness enemies use their abilities to catalogue them.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {data.abilities_seen.map(a => (
                <span key={a} className="font-mono text-[10px] uppercase tracking-widest text-[#B0ADA5] border border-[#333] px-2 py-1">{a}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Hunts tab ──────────────────────────────────────────────────
// Optional bounty contracts against unmet pillars of discovered enemies.
// One active at a time — pick, hunt, get paid.

const PILLAR_LABELS = {
  element: "Elemento",
  resistances: "Resistenze",
  weaknesses: "Debolezze",
  drops: "Drop",
  abilities: "Abilità",
};

function HuntsView({ hunts, onAccept, onAbandon, onReroll, busy, playerGold }) {
  if (!hunts) return <div className="italic text-xs text-[#666] p-4">Loading hunts…</div>;
  const active = hunts.active;
  const available = hunts.available || [];
  const rerollCost = hunts.reroll_cost ?? 30;
  const totalEarned = hunts.total_gold_earned || 0;
  const completed = hunts.completed_count || 0;
  const streak = hunts.streak || 0;
  const bestStreak = hunts.best_streak || 0;
  const nextMult = hunts.next_multiplier || 1.0;
  const curMult = hunts.current_multiplier || 1.0;
  const canReroll = (playerGold ?? 0) >= rerollCost && available.length > 0;
  const streakColor =
    streak >= 5 ? "text-[#E33333]" :
    streak >= 3 ? "text-[#E5C34A]" :
    streak >= 1 ? "text-[#B89C3A]" : "text-[#666]";

  return (
    <div className="space-y-2" data-testid="codex-hunts">
      {/* Bounty Board summary card — shows streak / total payout so the
          player sees the value they've built up over time. */}
      <div className="panel p-3" data-testid="hunt-board-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#B89C3A]">
              Bounty Board
            </div>
            <div className="font-mono text-[10px] text-[#8c8c8c] mt-1">
              Ricompense conservate dai vecchi cacciatori.
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-lg text-[#E5C34A]" data-testid="hunt-board-earned">
              {totalEarned.toLocaleString()}g
            </div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
              Guadagni totali
            </div>
          </div>
        </div>

        {/* Streak row — always visible so idle hunters see the reward
            they're missing out on. */}
        <div className="mt-3 pt-2 border-t border-[#161616] flex items-center justify-between gap-2"
             data-testid="hunt-board-streak-row">
          <div className="flex items-center gap-2">
            <Flame className={`w-3 h-3 ${streakColor}`} strokeWidth={1.5} />
            <span className={`font-heading text-xs tracking-widest uppercase ${streakColor}`}>
              Streak <span data-testid="hunt-streak-count">×{streak}</span>
            </span>
            {bestStreak > streak && (
              <span className="font-mono text-[9px] text-[#666] tracking-widest">
                (best ×{bestStreak})
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] text-[#E5C34A]" data-testid="hunt-next-multiplier">
              Prossima: ×{nextMult.toFixed(2)}
            </div>
            <div className="font-mono text-[9px] text-[#666] tracking-widest uppercase">
              {curMult > 1
                ? `Attuale ×${curMult.toFixed(2)}`
                : "Completa 2+ hunt per bonus"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-3 pt-2 border-t border-[#161616]">
          <div className="text-center">
            <div className="font-heading text-sm text-[#E2DFD8]" data-testid="hunt-board-completed">{completed}</div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">Completate</div>
          </div>
          <div className="text-center">
            <div className="font-heading text-sm text-[#E2DFD8]" data-testid="hunt-board-active">{active ? 1 : 0}</div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">Attiva</div>
          </div>
          <div className="text-center">
            <div className="font-heading text-sm text-[#E2DFD8]" data-testid="hunt-board-available">{available.length}</div>
            <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">Slate</div>
          </div>
        </div>
      </div>

      {active ? (
        <div className="panel p-3 border-l-2 border-[#E33333]" data-testid="codex-hunt-active">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#E33333]">Caccia in corso</div>
              <div className="font-heading text-sm text-[#E5C34A] mt-1">{active.enemy_name}</div>
              <div className="font-mono text-[10px] text-[#B0ADA5] mt-0.5">
                Rivela: <span className="text-[#E5C34A]">{PILLAR_LABELS[active.pillar] || active.pillar}</span>
              </div>
              <div className="font-mono text-[10px] text-[#B89C3A] mt-0.5">
                Ricompensa: +{active.reward_gold}g
              </div>
            </div>
            <button onClick={onAbandon} disabled={busy}
              data-testid="codex-hunt-abandon"
              className="px-2 py-1 border border-[#8A0303] text-[#E33333] hover:bg-[#8A0303]/10 font-mono text-[9px] tracking-widest uppercase flex items-center gap-1 disabled:opacity-40">
              <X className="w-3 h-3" /> Abbandona
            </button>
          </div>
        </div>
      ) : (
        <div className="panel p-3" data-testid="codex-hunt-none">
          <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">Nessuna caccia attiva</div>
          <div className="font-mono text-[10px] text-[#666] mt-1">
            Accetta una taglia qui sotto: rivela il pilastro richiesto sul nemico e riscuoti l'oro.
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-1">
        <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
          Taglie disponibili ({available.length})
        </div>
        <button onClick={onReroll} disabled={busy || !canReroll}
          data-testid="codex-hunt-reroll"
          title={!canReroll ? `Servono ${rerollCost}g e almeno una caccia sostituibile` : ""}
          className="px-2 py-1 border border-[#B89C3A] text-[#E5C34A] hover:bg-[#B89C3A]/10 font-mono text-[9px] tracking-widest uppercase disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1">
          <RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Rerolla ({rerollCost}g)
        </button>
      </div>

      {available.length === 0 ? (
        <div className="panel p-4 italic text-[#666] text-center text-xs">
          Nessuna caccia disponibile — combatti nuovi nemici per sbloccare taglie.
        </div>
      ) : (
        <div className="space-y-1">
          {available.map(h => (
            <div key={h.key} className="panel-inner p-2 flex items-center justify-between gap-2"
                 data-testid={`codex-hunt-row-${h.key}`}>
              <div className="min-w-0 flex-1">
                <div className="font-heading text-sm text-[#E2DFD8] truncate">{h.enemy_name}</div>
                <div className="font-mono text-[10px] text-[#8c8c8c] mt-0.5">
                  Lv {h.level}{h.rank ? ` · ${h.rank}` : ""} · Rivela <span className="text-[#E5C34A]">{PILLAR_LABELS[h.pillar] || h.pillar}</span>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="font-mono text-[10px] text-[#B89C3A]">+{h.reward_gold}g</div>
                <button onClick={() => onAccept(h.key)} disabled={busy || !!active}
                  data-testid={`codex-hunt-accept-${h.key}`}
                  title={active ? "Abbandona la caccia in corso per accettarne un'altra" : ""}
                  className="mt-1 px-2 py-0.5 border border-[#4A5D23] text-[#6E8B3D] hover:bg-[#4A5D23]/10 font-mono text-[9px] tracking-widest uppercase disabled:opacity-30">
                  Accetta
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
